import React, { useEffect, useRef, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { humanizeError } from "../utils/userFriendlyError";
import { useToast } from "../context/ToastContext";
import StatusBadge from "./StatusBadge";
import MarkNigoModal from "./MarkNigoModal";
import {
  isStatusEditable,
  getStatusEditorTargets,
  STATUS_LABELS,
} from "../constants/householdConstants";

function StatusEditor({
  household,
  userRole,
  onStatusUpdated,
  onNigoApplied,
}) {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [nigoModalOpen, setNigoModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [queryNoteOpen, setQueryNoteOpen] = useState(false);
  const [queryNote, setQueryNote] = useState("");
  const containerRef = useRef(null);

  const status = household?.status;
  const subStatus = household?.sub_status;
  const editable = isStatusEditable(userRole, status, subStatus);
  const nigoAccountCount = (household?.accounts || []).filter(
    (a) => a?.status === "nigo",
  ).length;

  useEffect(() => {
    if (!menuOpen) return;
    const onMouseDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [menuOpen]);

  if (!editable) {
    return <StatusBadge status={status} subStatus={subStatus} />;
  }

  const targets = getStatusEditorTargets(status);

  const performStatusChange = async (next, note) => {
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const trimmedNote = (note || "").trim();
      const payload = trimmedNote ? { status: next, note: trimmedNote } : { status: next };
      const response = await axiosInstance.put(
        `/households/${household.id}/status`,
        payload,
      );
      onStatusUpdated?.({
        status: response.data.status,
        sub_status: response.data.sub_status,
      });
      toast.success("Status updated.");
    } catch (err) {
      setErrorMessage(humanizeError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const applyStatus = async (next) => {
    setMenuOpen(false);
    if (!next || submitting) return;

    if (next === "nigo") {
      // Re-open the NIGO modal even if already NIGO so the user can edit
      // reasons/notes or add/remove accounts.
      setNigoModalOpen(true);
      return;
    }

    if (next === status) return;

    if (next === "under_dispute") {
      setQueryNote("");
      setQueryNoteOpen(true);
      return;
    }

    // Closing a household is irreversible — always confirm regardless of
    // current NIGO state.
    if (next === "closed") {
      setPendingStatus(next);
      return;
    }

    // Moving away from NIGO with existing NIGO accounts requires confirmation —
    // changing the household status will resolve every NIGO account.
    if (status === "nigo" && nigoAccountCount > 0) {
      setPendingStatus(next);
      return;
    }

    await performStatusChange(next);
  };

  const confirmRaiseQuery = async () => {
    setQueryNoteOpen(false);
    await performStatusChange("under_dispute", queryNote);
    setQueryNote("");
  };

  const confirmPending = async () => {
    const next = pendingStatus;
    setPendingStatus(null);
    if (next) await performStatusChange(next);
  };

  return (
    <div
      ref={containerRef}
      className="relative inline-flex flex-col items-start gap-1"
    >
      <button
        type="button"
        onClick={() => setMenuOpen((v) => !v)}
        disabled={submitting}
        className="inline-flex items-center gap-1 rounded-full transition-opacity disabled:opacity-60"
        aria-haspopup="listbox"
        aria-expanded={menuOpen}
        aria-label="Change household status"
      >
        <StatusBadge status={status} subStatus={subStatus} />
        <svg
          className={`h-3 w-3 text-gray-500 transition-transform ${menuOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
        {submitting && (
          <svg className="h-3 w-3 animate-spin text-emerald-600" fill="none" viewBox="0 0 24 24" aria-hidden>
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        )}
      </button>

      {menuOpen && (
        <div
          role="listbox"
          className="absolute left-0 top-full z-20 mt-1 min-w-[14rem] rounded-lg border border-emerald-200 bg-white shadow-lg ring-1 ring-emerald-100/60"
        >
          <ul className="max-h-72 overflow-y-auto py-1">
            {targets.map((target) => {
              const isCurrent = target === status;
              return (
                <li key={target}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isCurrent}
                    onClick={() => applyStatus(target)}
                    className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs font-medium transition-colors ${
                      isCurrent
                        ? "bg-emerald-50 text-emerald-900"
                        : "text-gray-800 hover:bg-emerald-50"
                    }`}
                  >
                    <span>{STATUS_LABELS[target] || target}</span>
                    {isCurrent && (
                      <svg
                        className="h-3.5 w-3.5 text-emerald-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {errorMessage && (
        <div className="rounded-md border border-red-200 bg-red-50 px-2 py-1 text-[11px] text-red-800">
          {errorMessage}
        </div>
      )}

      <MarkNigoModal
        isOpen={nigoModalOpen}
        onClose={() => setNigoModalOpen(false)}
        householdId={household?.id}
        accounts={household?.accounts}
        members={household?.members}
        onSuccess={(data) => {
          if (data?.status) {
            onStatusUpdated?.({
              status: data.status,
              sub_status: data.sub_status ?? null,
            });
          }
          onNigoApplied?.();
        }}
      />

      {queryNoteOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="raise-query-title"
          onClick={() => setQueryNoteOpen(false)}
        >
          <div
            className="w-full max-w-md overflow-hidden rounded-2xl border border-amber-100 bg-white shadow-2xl shadow-amber-900/10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-amber-100 bg-gradient-to-r from-amber-50/90 to-orange-50/50 px-5 py-4">
              <h3 id="raise-query-title" className="text-base font-bold text-amber-950">
                Raise a query on this household?
              </h3>
            </div>
            <div className="px-5 py-4 text-sm text-gray-700">
              <p className="mb-3">
                The household moves to <span className="font-semibold">Query Raised</span> and a note is added to its comments.
              </p>
              <textarea
                value={queryNote}
                onChange={(e) => setQueryNote(e.target.value)}
                maxLength={400}
                rows={3}
                autoFocus
                placeholder="What is the query? (optional)"
                className="w-full rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm text-gray-800 focus:border-amber-400 focus:outline-none"
              />
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-100 bg-gray-50/60 px-5 py-3">
              <button
                type="button"
                onClick={() => setQueryNoteOpen(false)}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmRaiseQuery}
                className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-amber-700"
              >
                Raise query
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingStatus && pendingStatus === "closed" && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-close-title"
          onClick={() => setPendingStatus(null)}
        >
          <div
            className="w-full max-w-md overflow-hidden rounded-2xl border border-red-100 bg-white shadow-2xl shadow-red-900/10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-red-100 bg-gradient-to-r from-red-50/90 to-rose-50/50 px-5 py-4">
              <h3
                id="confirm-close-title"
                className="text-base font-bold text-red-950"
              >
                Close this household?
              </h3>
            </div>
            <div className="px-5 py-4 text-sm text-gray-700">
              Are you sure you want to close this household? This action cannot
              be undone.
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-100 bg-gray-50/60 px-5 py-3">
              <button
                type="button"
                onClick={() => setPendingStatus(null)}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmPending}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-red-700"
              >
                Close household
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingStatus && pendingStatus !== "closed" && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-away-nigo-title"
          onClick={() => setPendingStatus(null)}
        >
          <div
            className="w-full max-w-md overflow-hidden rounded-2xl border border-amber-100 bg-white shadow-2xl shadow-amber-900/10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-amber-100 bg-gradient-to-r from-amber-50/90 to-orange-50/50 px-5 py-4">
              <h3
                id="confirm-away-nigo-title"
                className="text-base font-bold text-amber-950"
              >
                Resolve all NIGO accounts?
              </h3>
            </div>
            <div className="px-5 py-4 text-sm text-gray-700">
              Changing the household status to{" "}
              <span className="font-semibold">
                {STATUS_LABELS[pendingStatus] || pendingStatus}
              </span>{" "}
              will mark{" "}
              <span className="font-semibold text-red-700">
                {nigoAccountCount} NIGO account
                {nigoAccountCount === 1 ? "" : "s"}
              </span>{" "}
              as resolved. This cannot be undone from this dialog.
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-100 bg-gray-50/60 px-5 py-3">
              <button
                type="button"
                onClick={() => setPendingStatus(null)}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmPending}
                className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-amber-700"
              >
                Resolve and continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StatusEditor;
